<template>
  <cac-page class="candidate-profile-page">
    <cac-page-header 
      slot="pre-content" 
      :title="pageTitle" 
      class="candidate-profile-page___header"
      centered
    />

    <div class="col">
      <div class="row justify-content-center">
        <div
          v-if="isLoading"
          class="col-18 mb-5 d-flex justify-content-center"
        >
          <spinner />
        </div>
        <div 
          v-if="profileData"
          class="col-12 col-md-7 col-lg-6"
        >
          <official-profile-card 
            :official="profileData"
          />
        </div>

        <div 
          v-if="hasContentSection"
          class="col-14 col-md-10 offset-md-1 offset-lg-2"
        >
          <page-section
            v-if="hasClientSection"
            id="candidate-profile__client-data"
            :title="profileData.clientData.title"
            class="candidate-profile__section candidate-profile__client-data"
            header-class="text-center text-md-left"
          >
            <p>{{ profileData.clientData.text }}</p>
          </page-section>

          <page-section
            v-if="hasBioSection"
            id="candidate-profile-page__bio"
            :title="$t('cac.app.bio')"
            class="candidate-profile__section candidate-profile__bio"
            header-class="text-center text-md-left"
          >
            <p 
              v-for="{ key, value } of bioDetails"
              :key="key"
            >
              <strong>{{ $t(`cac.app.${key}`) }}:</strong>
              {{ value }}
            </p>
            <div 
              v-if="clientUploadedImage" 
              class="candidate-profile__image-wrapper">
              <img 
                :src="clientUploadedImage" 
                class="candidate-profile__image">
            </div>
          </page-section>
        </div>
      </div>
    </div>
  </cac-page>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { isEmpty as _isEmpty } from "lodash";
import Routes from "Constants/campaign/civicactioncenter/routeNames";
import STATE from "Constants/campaign/civicactioncenter/state";
import CacPage from "CivicActionCenterComponents/layout/CACPage.vue";
import CacPageHeader from "CivicActionCenterComponents/layout/CACPageHeader.vue";
import PageSection from "CivicActionCenterComponents/shared/PageSection.vue";
import OfficialProfileCard from "CivicActionCenterComponents/shared/OfficialProfileCard.vue";
import Spinner from "CivicActionCenterComponents/utilities/Spinner.vue";

export default {
  name: "CandidateProfile",
  components: {
    CacPage,
    CacPageHeader,
    PageSection,
    OfficialProfileCard,
    Spinner
  },
  inject: {
    apiHelper: { from: "apiHelper" }
  },
  data() {
    return {
      isLoading: true
    };
  },
  computed: {
    ...mapGetters(STATE.SCOPE_OFFICIALS, {
      currentCandidate: STATE.CURRENT_CANDIDATE_DATA,
      currentOfficial: STATE.CURRENT_OFFICIAL_DATA
    }),
    isCandidateProfile() {
      return this.$route.name === Routes.CANDIDATE_PROFILE;
    },
    officialId() {
      return this.$route.params.id;
    },
    profileData() {
      if (this.isCandidateProfile) {
        return this.currentCandidate;
      }

      return this.currentOfficial;
    },
    hasClientSection() {
      return !!(
        this.profileData.clientData && this.profileData.clientData.text
      );
    },
    clientUploadedImage() {
      if (this.profileData.images && this.profileData.images.length > 0) {
        const candidateId = this.profileData.id;
        const filename = this.profileData.images[0].split("/").pop();

        return this.apiHelper.getCandidateImageUrl(candidateId, filename);
      }
      return false;
    },
    bioDetails() {
      return ["education", "experience"]
        .map(key => ({
          key,
          value: this.profileData[key]
        }))
        .filter(detail => !!detail.value);
    },
    hasBioSection() {
      // Removed biography text for PDEV 13293
      // return !_isEmpty(this.profileData.biography || this.bioDetails);
      return !_isEmpty(this.bioDetails);
    },
    hasContentSection() {
      return this.hasClientSection || this.hasBioSection;
    },
    pageTitle() {
      const { name = "", title = "", race = "" } = this.profileData;
      if (this.isCandidateProfile) {
        if (!race) {
          return "";
        }

        const raceName = this.$t(`cac.app.${race}`);
        return `${this.$t("cac.app.candidate_for")} ${raceName}`;
      }

      return `${title} ${name}`;
    }
  },
  watch: {
    officialId() {
      this.checkForFullProfile();
    }
  },
  created() {
    this.checkForFullProfile();
  },
  methods: {
    ...mapActions(STATE.SCOPE_OFFICIALS, {
      setCurrentCandidate: STATE.CURRENT_CANDIDATE_ID,
      setCurrentOfficial: STATE.CURRENT_OFFICIAL_ID
    }),
    async checkForFullProfile() {
      if (!this.officialId) {
        return;
      }

      this.setOfficialIdInStore();

      if (!this.profileData) {
        this.isLoading = true;
        try {
          await this.fetchProfileData();
        } finally {
          this.isLoading = false;
        }
      }
    },
    fetchProfileData() {
      if (this.isCandidateProfile) {
        return this.apiHelper.getCandidate(this.officialId);
      }

      return this.apiHelper.getLegislator(this.officialId);
    },
    setOfficialIdInStore() {
      if (this.isCandidateProfile) {
        this.setCurrentCandidate(this.officialId);
      } else {
        this.setCurrentOfficial(this.officialId);
      }
    }
  }
};
</script>
